import { useCallback, useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { validateDateRange, validateWorkDate } from 'modules/permit/utils/permitHelpers'
import type { PermitFormState } from '../types'

const TIME_HOUR_OPTIONS = Array.from({ length: 24 }, (_, index) => index.toString().padStart(2, '0'))
// Minutes advance in 5-minute increments to keep choices manageable in dropdowns.
const TIME_MINUTE_OPTIONS = Array.from({ length: 12 }, (_, index) =>
  (index * 5).toString().padStart(2, '0'),
)

type TimeParts = {
  hour: string
  minute: string
}

export const parseTimeParts = (value?: string | null): TimeParts => {
  if (!value || typeof value !== 'string' || !value.includes(':')) {
    return { hour: '', minute: '' }
  }
  const [rawHour = '', rawMinute = ''] = value.split(':')
  return {
    hour: rawHour.padStart(2, '0').slice(-2),
    minute: rawMinute.padStart(2, '0').slice(-2),
  }
}

export type PermitDateErrors = {
  startDate: string
  endDate: string
  dateRange: string
}

interface UsePermitDateTimeParams {
  permitData: PermitFormState
  setPermitData: Dispatch<SetStateAction<PermitFormState>>
}

export const usePermitDateTime = ({
  permitData,
  setPermitData,
}: UsePermitDateTimeParams) => {
  const [dateErrors, setDateErrors] = useState<PermitDateErrors>({
    startDate: '',
    endDate: '',
    dateRange: '',
  })

  const resetDateErrors = useCallback(() => {
    setDateErrors({ startDate: '', endDate: '', dateRange: '' })
  }, [])

  const validateDates = useCallback(
    (startDate: string, endDate: string) => {
      const errors: PermitDateErrors = { startDate: '', endDate: '', dateRange: '' }

      const startValidation = validateWorkDate(startDate)
      if (!startValidation.isValid) {
        errors.startDate = startValidation.error || ''
      }

      const endValidation = validateWorkDate(endDate)
      if (!endValidation.isValid) {
        errors.endDate = endValidation.error || ''
      }

      if (startValidation.isValid && endValidation.isValid && startDate && endDate) {
        const rangeValidation = validateDateRange(startDate, endDate)
        if (!rangeValidation.isValid) {
          errors.dateRange = rangeValidation.error || ''
        }
      }

      setDateErrors(errors)
      return !errors.startDate && !errors.endDate && !errors.dateRange
    },
    [],
  )

  const handleStartDateChange = useCallback(
    (date: string) => {
      setPermitData((prev) => ({ ...prev, startDate: date }))
      validateDates(date, permitData.endDate)
    },
    [permitData.endDate, setPermitData, validateDates],
  )

  const handleEndDateChange = useCallback(
    (date: string) => {
      setPermitData((prev) => ({ ...prev, endDate: date }))
      validateDates(permitData.startDate, date)
    },
    [permitData.startDate, setPermitData, validateDates],
  )

  const updateTimeField = useCallback(
    (fieldKey: 'startTime' | 'endTime', updates: Partial<TimeParts>) => {
      setPermitData((prev) => {
        const existingValue: string = (prev as any)?.[fieldKey] ?? ''
        const currentParts = parseTimeParts(existingValue)
        const nextHour = updates.hour ?? currentParts.hour
        const nextMinute = updates.minute ?? currentParts.minute

        if (!nextHour) {
          return { ...prev, [fieldKey]: '' }
        }

        const normalizedMinute = nextMinute || '00'
        return {
          ...prev,
          [fieldKey]: `${nextHour}:${normalizedMinute}`,
        }
      })
    },
    [setPermitData],
  )

  const handleTimeHourChange = useCallback(
    (fieldKey: 'startTime' | 'endTime', hourValue: string) => {
      updateTimeField(fieldKey, { hour: hourValue })
    },
    [updateTimeField],
  )

  const handleTimeMinuteChange = useCallback(
    (fieldKey: 'startTime' | 'endTime', minuteValue: string) => {
      updateTimeField(fieldKey, { minute: minuteValue })
    },
    [updateTimeField],
  )

  return {
    dateErrors,
    resetDateErrors,
    validateDates,
    handleStartDateChange,
    handleEndDateChange,
    handleTimeHourChange,
    handleTimeMinuteChange,
    timeHourOptions: TIME_HOUR_OPTIONS,
    timeMinuteOptions: TIME_MINUTE_OPTIONS,
  }
}
